import React from 'react';
import { Container, Box, Typography, Paper, Divider, Grid, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GppGoodIcon from '@mui/icons-material/GppGood';
import SecurityIcon from '@mui/icons-material/Security';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useTranslation } from 'react-i18next';
import { useRegion } from '../../context/RegionContext';

// Data Privacy and Compliance Configuration
const PRIVACY_CONFIG = {
  UK: {
    regulator: 'Information Commissioner\'s Office (ICO)',
    legalBasis: 'Legitimate Interest and Contractual Necessity',
    retentionPeriod: '7 years for financial records, 3 years for other data',
    subjectAccessRequest: 'Within 30 days, free of charge for first request'
  },
  EU: {
    regulator: 'European Data Protection Board (EDPB)',
    representative: 'EU Data Protection Representative Ltd',
    representativeContact: 'representative@fluxerp.com',
    legalBasis: 'GDPR Article 6(1)(b) - Contract Performance',
    retentionPeriod: '10 years for financial records, 5 years for other data',
    subjectAccessRequest: 'Within 30 days, free of charge'
  },
  US: {
    regulator: 'Federal Trade Commission (FTC)',
    compliance: 'CCPA, COPPA, GLBA compliant',
    retentionPeriod: '7 years for financial records, 4 years for other data',
    subjectAccessRequest: 'Within 45 days, free of charge for first 2 requests annually'
  },
  default: {
    regulator: 'Local Data Protection Authority',
    retentionPeriod: '7 years for financial records, 3 years for other data',
    subjectAccessRequest: 'Within 30 days'
  }
};

const TermsOfServicePage = () => {
  const { t } = useTranslation();
  const seo = getPageSEO('terms');
  const { contact, region } = useRegion();
  const privacyConfig = PRIVACY_CONFIG[region] || PRIVACY_CONFIG.default;

  // Service Level Agreement - simplified
  const slaMetrics = [
    { metric: 'Uptime Commitment', value: 'Commercial Best Efforts', description: 'Subject to Enterprise Agreement' },
    { metric: 'Support Response', value: 'Best Effort', description: 'During business hours' }
  ];

  // Security compliance
  const securityCompliance = [
    { name: 'ISO 27001 Compliant', description: 'Information Security Management Standards' },
    { name: 'SOC 2 Compliant', description: 'Security, Availability, Confidentiality' },
    { name: 'GDPR Compliant', description: 'EU Data Protection Standards' },
    { name: 'PCI DSS Compliant', description: 'Payment Card Industry Standards' }
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />

      <HeroSection chip={t('website.terms.heroChip')}>
        <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 52 }, fontWeight: 800, mb: 2, color: 'primary.contrastText' }}>
            Terms of Use & Service Agreement
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, color: 'primary.contrastText', mb: 3 }}>
            Terms & Conditions for Cloud ERP Solutions
          </Typography>

        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Executive Summary */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            📋 Executive Summary
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using FluxERP, a comprehensive cloud-based ERP solution provided by <strong>Flux IT Studio LTD</strong>, you agree to be bound by these Terms of Service.
            These terms constitute a legally binding agreement between your organization and Flux IT Studio LTD.
          </Typography>
          <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>
            <strong>Quick Links:</strong>
            <Box component="span" sx={{ ml: 1 }}>
              <a href="#subscription" style={{ color: 'inherit', marginRight: 16 }}>Subscription & Pricing</a>
              <a href="#refund" style={{ color: 'inherit', marginRight: 16 }}>Refund Policy</a>
              <a href="#data-privacy" style={{ color: 'inherit', marginRight: 16 }}>Data Privacy</a>
              <a href="#security" style={{ color: 'inherit' }}>Security</a>
            </Box>
          </Alert>
        </Paper>

        <Box sx={{
          '& h2': { fontSize: 28, fontWeight: 700, mt: 6, mb: 3, color: 'text.primary' },
          '& h3': { fontSize: 20, fontWeight: 600, mt: 4, mb: 2, color: 'text.primary' },
          '& h4': { fontSize: 18, fontWeight: 600, mt: 3, mb: 1.5, color: 'text.primary' },
          '& p': { mb: 2, lineHeight: 1.8, color: 'text.secondary' },
          '& ul': { mb: 2, pl: 3, color: 'text.secondary' },
          '& li': { mb: 1, lineHeight: 1.7 },
          '& .MuiPaper-root': { mb: 3 }
        }}>

          {/* 1. DEFINITIONS */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h2" sx={{ mt: 0, mb: 0 }}>1. Definitions & Interpretation</Typography>
            </Box>
            <Grid container spacing={3}>
              {[
                { term: '"We", "Our", "Us"', definition: 'Flux IT Studio LTD, the provider of the FluxERP service.' },
                { term: '"You", "Your", "Customer"', definition: 'The legal entity or individual using the FluxERP service under these terms.' },
                { term: '"Service", "Platform", "FluxERP"', definition: 'The cloud-based ERP software solution, including all features, updates, and documentation.' },
                { term: '"Subscription"', definition: 'The paid service plan selected by you, governed by these terms.' },
                { term: '"Data", "Content"', definition: 'All information, files, and materials uploaded or processed through the Service.' },
                { term: '"Confidential Information"', definition: 'All non-public information disclosed by either party, including business plans, financial data, and customer lists.' },
                { term: '"Affiliate"', definition: 'Any entity that controls, is controlled by, or is under common control with a party.' },
                { term: '"Force Majeure"', definition: 'Events beyond reasonable control including natural disasters, war, terrorism, government actions, and Internet failures.' }
              ].map((item, idx) => (
                <Grid item xs={12} md={6} key={idx}>
                  <Typography variant="subtitle1" fontWeight={700}>{item.term}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.definition}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* 2. SERVICE DESCRIPTION & FEATURES */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>2. Service Description & Features</Typography>
            <Typography variant="body1" paragraph>
              FluxERP provides a comprehensive enterprise-grade cloud-based ERP platform encompassing:
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ fontWeight: 600, mt: 0 }}>Core Modules</Typography>
                <ul>
                  <li><strong>Financial Management:</strong> General Ledger, Accounts Payable/Receivable, Multi-currency, Bank Reconciliation, Fixed Assets</li>
                  <li><strong>Inventory & Supply Chain:</strong> Real-time Inventory Tracking, Purchase Orders, Supplier Management, Warehouse Management</li>
                  <li><strong>Sales & CRM:</strong> Opportunity Management, Quote-to-Cash, Customer Portal, Service Management</li>
                  <li><strong>HR & Payroll:</strong> Employee Management, Leave Tracking, Payroll Processing, Performance Reviews</li>
                  <li><strong>Project Management:</strong> Resource Allocation, Time Tracking, Project Accounting, Budget Management</li>
                </ul>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ fontWeight: 600, mt: 0 }}>Advanced Features</Typography>
                <ul>
                  <li><strong>Analytics & BI:</strong> Real-time Dashboards, Custom Reports, AI-Powered Insights, Predictive Analytics</li>
                  <li><strong>Integration Suite:</strong> RESTful API, Webhooks, EDI Support, Third-party Integrations (Banking, Payment Gateways, E-commerce)</li>
                  <li><strong>Automation:</strong> Workflow Automation, Approval Chains, Scheduled Jobs, Email Notifications</li>
                  <li><strong>Multi-Entity:</strong> Multi-company, Multi-currency, Multi-language, Global Tax Compliance</li>
                  <li><strong>Mobile Access:</strong> Native iOS/Android Apps, Progressive Web App, Offline Support</li>
                </ul>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <strong>Changes to the Service:</strong> We may modify, improve, replace or discontinue features of the Service from time to time. We will provide reasonable notice of material changes that may adversely affect your use of the Service.
            </Alert>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Availability Disclaimer:</strong> We do not guarantee uninterrupted or error-free operation, although we will use commercially reasonable efforts to maintain availability.
            </Alert>
          </Paper>

          {/* 3. SUBSCRIPTION & PAYMENT TERMS - Updated */}
          <Paper sx={{ p: 4, mb: 4 }} id="subscription">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PaymentIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h2" sx={{ mt: 0, mb: 0 }}>3. Subscription & Payment Terms</Typography>
            </Box>

            <Typography variant="h3">3.1 Subscription Plans</Typography>
            <Typography variant="body1" paragraph>
              Subscription plans, features, usage limits and pricing are available on our Pricing page and may be updated from time to time.
            </Typography>
            <Typography variant="body1" paragraph>
              The specific subscription plan, fees, and billing terms applicable to your account will be set out in your Order Form or subscription confirmation.
            </Typography>

            <Typography variant="h3">3.2 Billing & Invoicing</Typography>
            <ul>
              <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually in advance as specified in your Order Form.</li>
              <li><strong>Payment Methods:</strong> We accept major credit cards (Visa, Mastercard, Amex), bank transfers (ACH/SEPA), and PayPal. Enterprise customers may opt for invoice-based billing with NET 30 terms (subject to credit approval).</li>
              <li><strong>Taxes:</strong> All prices are exclusive of applicable taxes, including VAT, GST, or sales tax. Customers are responsible for providing valid tax exemption certificates.</li>
              <li><strong>Price Changes:</strong> We provide a minimum 60 days written notice for any price adjustments affecting active subscriptions. You may terminate your subscription without penalty within 30 days of receiving a price increase notice.</li>
              <li><strong>Overdue Payments:</strong> Late payments shall accrue interest at 1.5% per month (or the maximum legal rate). Suspension of service occurs after 15 days of non-payment, with termination after 30 days.</li>
            </ul>
          </Paper>

          {/* 4. COMPREHENSIVE REFUND POLICY - Updated */}
          <Paper sx={{ p: 4, mb: 4 }} id="refund">
            <Typography variant="h2" sx={{ color: 'primary.main' }}>4. Comprehensive Refund & Cancellation Policy</Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>4.1 Subscription & Refund Policy</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  FluxERP services and subscriptions are tailored to your enterprise deployment and onboarding schedule.
                </Typography>
                <ul>
                  <li>Subscription fees are non-refundable once charged to your account, except where required by applicable law</li>
                  <li>Customers may cancel their subscription at any time, with cancellation taking effect at the conclusion of the current billing term</li>
                  <li>No prorated refunds will be issued for unused portions of active terms</li>
                </ul>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please ensure you fully evaluate the Service and features during your demonstration and sales onboarding. All sales are final once an order form or subscription is active.
                </Alert>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>4.2 Annual Subscriptions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul>
                  <li><strong>No prorated refunds</strong> are issued for early termination of annual subscriptions</li>
                  <li>Service continues until the end of the current billing year</li>
                  <li>Early termination charges may apply where agreed in the applicable Order Form or Enterprise Agreement</li>
                  <li>Transition to monthly billing is available at the next renewal with a 30-day notice</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>4.3 Professional Services</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  Fees for professional services including implementation, custom development, data migration, and training are <strong>strictly non-refundable</strong> once service delivery has commenced.
                </Typography>
                <ul>
                  <li>Pre-paid consulting hours expire after 12 months</li>
                  <li>Any unused prepaid hours may be applied to alternative professional services</li>
                  <li>Custom development requests require 50% deposit, non-refundable</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h3" sx={{ mt: 0, mb: 0 }}>4.4 Discounts</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  Discounts offered by Flux IT Studio LTD:
                </Typography>
                <ul>
                  <li><strong>Annual Commitment:</strong> Discount on annual subscriptions compared to monthly billing (see Pricing page)</li>
                  <li><strong>Volume Discounts:</strong> Available for Enterprise customers with 10+ users or multiple companies</li>
                  <li><strong>Non-Profit Discount:</strong> Subject to verification for registered non-profit organizations</li>
                  <li><strong>Educational Discount:</strong> Subject to verification for accredited educational institutions</li>
                  <li>All discounts are applied at the time of purchase and cannot be retroactively applied</li>
                  <li>Discounts may be revoked if terms are violated or payment is delinquent</li>
                </ul>
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* 5. DATA PRIVACY & PROTECTION - Simplified */}
          <Paper sx={{ p: 4, mb: 4 }} id="data-privacy">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <DataUsageIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h2" sx={{ mt: 0, mb: 0 }}>5. Data Privacy & Protection</Typography>
            </Box>

            <Typography variant="body1" paragraph>
              We take data privacy and protection seriously. Our practices are designed to comply with applicable data protection laws including GDPR.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h3">5.1 Data Ownership</Typography>
                <Typography variant="body1">
                  You retain full ownership and intellectual property rights to all data, information, and materials you input into FluxERP.
                </Typography>
                <ul>
                  <li>We do not claim ownership of your business data</li>
                  <li>We do not sell or rent your data to third parties</li>
                  <li>You have the right to export your data at any time in standard formats</li>
                  <li>Data can be transferred to other platforms via our API</li>
                </ul>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h3">5.2 Data Processing</Typography>
                <Typography variant="body1">
                  By using the Service, you grant us the right to:
                </Typography>
                <ul>
                  <li>Store, process, and transmit your data exclusively for the purpose of providing the Service</li>
                  <li>Use aggregated, anonymized data for system analytics and performance monitoring</li>
                  <li>Create secure, encrypted backups of your data</li>
                  <li>Process data in accordance with our Data Processing Agreement</li>
                </ul>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              For complete details on how we handle your data, please review our <strong>Privacy Policy</strong> and <strong>Data Processing Agreement</strong>.
            </Alert>
          </Paper>

          {/* 6. ACCEPTABLE USE POLICY */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>6. Acceptable Use Policy</Typography>
            <Typography variant="body1" paragraph>
              You agree to use the Service in compliance with all applicable laws and regulations. Prohibited activities include:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                  <Typography variant="subtitle1" fontWeight={700}>🚫 Prohibited Activities</Typography>
                  <ul>
                    <li>Hacking, unauthorized access, or security breaches</li>
                    <li>Uploading malware, viruses, or malicious code</li>
                    <li>Crypto mining or resource-intensive activities</li>
                    <li>Sending spam or unsolicited communications</li>
                    <li>Illegal activities of any kind</li>
                    <li>Copyright or intellectual property infringement</li>
                    <li>Reverse engineering or decompiling the Service</li>
                    <li>Attempting to circumvent usage limits or restrictions</li>
                  </ul>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="subtitle1" fontWeight={700}>✅ Permitted Use</Typography>
                  <ul>
                    <li>Internal business operations</li>
                    <li>Legitimate accounting and financial management</li>
                    <li>Authorized user access as per your subscription plan</li>
                    <li>Standard API usage within documented limits</li>
                    <li>Custom integrations with approved third-party services</li>
                  </ul>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* 7. ACCOUNT SECURITY */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>7. Account Security</Typography>
            <Typography variant="body1" paragraph>
              You are responsible for maintaining the security of your account credentials and for all activities that occur under your account.
            </Typography>
            <ul>
              <li><strong>Password Management:</strong> You are responsible for creating and maintaining strong, secure passwords</li>
              <li><strong>Multi-Factor Authentication:</strong> We strongly recommend enabling MFA for all user accounts</li>
              <li><strong>Access Control:</strong> You are responsible for managing user permissions and revoking access when necessary</li>
              <li><strong>Security Incidents:</strong> You must immediately report any suspected security breach or unauthorized access to support@fluxerp.co.uk</li>
              <li><strong>Shared Responsibility:</strong> While we protect the platform, you are responsible for securing your internal network and end-user devices</li>
            </ul>
          </Paper>

          {/* 8. SUSPENSION */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>8. Suspension of Service</Typography>
            <Typography variant="body1">
              We reserve the right to suspend or terminate your access to the Service immediately without prior notice for:
            </Typography>
            <ul>
              <li>Violation of these Terms of Service</li>
              <li>Engaging in prohibited activities under the Acceptable Use Policy</li>
              <li>Non-payment or delinquent payment exceeding 30 days</li>
              <li>Security threats to the Service or other customers</li>
              <li>Legal or regulatory requirements</li>
              <li>Fraudulent or illegal activities</li>
            </ul>
            <Alert severity="warning" sx={{ mt: 2 }}>
              During suspension, you will be unable to access your data. We will make reasonable efforts to notify you and provide a grace period where feasible.
            </Alert>
          </Paper>

          {/* 9. BETA FEATURES */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>9. Beta Features</Typography>
            <Typography variant="body1" paragraph>
              We may offer pre-release, beta, or experimental features ("Beta Features") as part of the Service.
            </Typography>
            <ul>
              <li><strong>AI Features:</strong> AI-powered features (including the AI Assistant) are provided as Beta Features</li>
              <li><strong>Assistance Only:</strong> AI-generated outputs are provided for assistance only and should be reviewed by users before making business, accounting, tax, legal or financial decisions</li>
              <li><strong>No Liability:</strong> Beta Features are provided "AS IS" without warranties and may be discontinued at any time</li>
              <li><strong>Feedback:</strong> By using Beta Features, you grant us a perpetual, worldwide, royalty-free licence to use feedback to improve the Service</li>
              <li><strong>Limitations:</strong> Beta Features may contain errors, have limited functionality, or experience performance issues</li>
            </ul>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>AI Disclaimer:</strong> AI-generated outputs are for assistance only. Always verify AI-generated information before making business, accounting, tax, legal, or financial decisions.
            </Alert>
          </Paper>

          {/* 10. THIRD-PARTY SERVICES */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>10. Third-Party Services</Typography>
            <Typography variant="body1">
              The Service integrates with or relies on various third-party services and providers:
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Microsoft</Typography>
                  <Typography variant="caption">Azure Cloud Services</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Google</Typography>
                  <Typography variant="caption">Cloud Platform & Workspace</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Stripe</Typography>
                  <Typography variant="caption">Payment Processing</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>PayPal</Typography>
                  <Typography variant="caption">Payment Gateway</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>OpenAI</Typography>
                  <Typography variant="caption">AI Services</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={600}>AWS</Typography>
                  <Typography variant="caption">Cloud Infrastructure</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ mt: 2 }}>
              We are not responsible for the practices, privacy policies, or performance of these third-party services.
              Your use of third-party services is subject to their respective terms and conditions.
            </Typography>
          </Paper>

          {/* 11. INTELLECTUAL PROPERTY */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>11. Intellectual Property Rights</Typography>
            <Typography variant="body1">
              FluxERP, the FluxERP logo, and all related software, code, designs, and content are the exclusive property of
              <strong> Flux IT Studio LTD</strong>. All rights reserved.
            </Typography>
            <ul>
              <li>You may not copy, modify, distribute, or use our intellectual property without express written permission</li>
              <li>User-generated content (reports, dashboards, custom fields) are owned by the customer</li>
              <li>By providing feedback, suggestions, or ideas, you grant Flux IT Studio Ltd a perpetual, worldwide, royalty-free licence to use that feedback to improve the Service</li>
              <li>Third-party intellectual property rights must be respected, including open-source licenses</li>
            </ul>
          </Paper>

          {/* 12. TERMINATION & DATA EXTRACTION */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>12. Termination & Data Extraction</Typography>

            <Typography variant="h3">12.1 Termination by Customer</Typography>
            <ul>
              <li>Cancel through account settings or by contacting support</li>
              <li>Effective at the end of the current billing cycle</li>
              <li>No early termination fees for monthly subscriptions (unless agreed in Enterprise Agreement)</li>
              <li>Annual subscriptions continue until the end of the commitment period</li>
            </ul>

            <Typography variant="h3">12.2 Termination by Provider</Typography>
            <ul>
              <li>Material breach of Terms of Service</li>
              <li>Non-payment exceeding 30 days</li>
              <li>Fraudulent, illegal, or abusive activities</li>
              <li>Security risk to other customers or the platform</li>
            </ul>

            <Typography variant="h3">12.3 Data Extraction & Grace Period</Typography>
            <ul>
              <li><strong>Grace Period:</strong> 60 days for Enterprise customers, 30 days for all others</li>
              <li><strong>Data Export:</strong> Comprehensive export includes all financial data, master data, and transaction history</li>
              <li><strong>Export Formats:</strong> CSV, JSON, XML, PDF, and standard accounting formats</li>
              <li><strong>API Access:</strong> Maintained during the grace period</li>
              <li><strong>Data Deletion:</strong> Permanently removed after grace period expires</li>
            </ul>
          </Paper>

          {/* 13. FORCE MAJEURE */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>13. Force Majeure</Typography>
            <Typography variant="body1">
              Neither party shall be liable for any failure or delay in performance of its obligations under these Terms (except for payment obligations) where such failure or delay arises from any cause beyond the reasonable control of that party, including but not limited to:
            </Typography>
            <ul>
              <li>Natural disasters, earthquakes, floods, fires, storms, or similar events</li>
              <li>War, terrorism, riots, civil unrest, or similar disturbances</li>
              <li>Government actions, regulations, embargoes, or sanctions</li>
              <li>Internet service provider failures, cyber-attacks, or telecommunications failures</li>
              <li>Power outages or infrastructure failures</li>
              <li>Pandemics, epidemics, or public health emergencies</li>
              <li>Industrial disputes, strikes, or labor shortages</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2 }}>
              The affected party shall promptly notify the other party and make reasonable efforts to mitigate the effects of the Force Majeure event. If the Force Majeure event continues for more than 30 days, either party may terminate the affected subscription without liability.
            </Typography>
          </Paper>

          {/* 14. EXPORT CONTROL */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>14. Export Control</Typography>
            <Typography variant="body1">
              You acknowledge that the Service may be subject to export control laws and regulations, including but not limited to:
            </Typography>
            <ul>
              <li><strong>UK Export Control Regulations</strong></li>
              <li><strong>US Export Administration Regulations (EAR)</strong></li>
              <li><strong>EU Dual-Use Regulations</strong></li>
              <li><strong>International Traffic in Arms Regulations (ITAR)</strong> - where applicable</li>
            </ul>
            <Typography variant="body1">
              You agree not to use, transfer, or export the Service in violation of any applicable export control laws. You are responsible for compliance with all applicable export control regulations in your jurisdiction.
            </Typography>
          </Paper>

          {/* 15. LIABILITY & DISCLAIMERS */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>15. Limitation of Liability & Disclaimers</Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>IMPORTANT:</strong> The following limitations apply to the maximum extent permitted by applicable law.
            </Alert>

            <Typography variant="h3">15.1 Disclaimer of Warranties</Typography>
            <Typography variant="body1">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. WE EXPRESSLY DISCLAIM ALL WARRANTIES,
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </Typography>

            <Typography variant="h3">15.2 Limitation of Liability</Typography>
            <ul>
              <li>TO THE MAXIMUM EXTENT PERMITTED BY LAW, FLUX IT STUDIO LTD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>OUR TOTAL CUMULATIVE LIABILITY SHALL NOT EXCEED THE TOTAL AMOUNT PAID IN THE 12 MONTHS PRECEDING THE CLAIM</li>
              <li>WE ARE NOT LIABLE FOR LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL</li>
              <li>WE ARE NOT LIABLE FOR UNAUTHORIZED ACCESS TO OR ALTERATION OF TRANSMISSIONS OR DATA</li>
            </ul>
          </Paper>

          {/* 16. GOVERNING LAW - Updated */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>16. Governing Law & Dispute Resolution</Typography>

            <Typography variant="body1" paragraph>
              {region === 'PK' ? (
                <>These terms shall be governed by and construed in accordance with the laws of <strong>Pakistan</strong>. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts of Pakistan.</>
              ) : region === 'UK' ? (
                <>These terms shall be governed by and construed in accordance with the laws of <strong>England and Wales</strong>. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts of England and Wales.</>
              ) : region === 'EU' ? (
                <>These terms shall be governed by the laws of <strong>Ireland</strong> (our EU headquarters jurisdiction). Disputes shall be resolved through binding arbitration in accordance with the rules of the International Chamber of Commerce (ICC).</>
              ) : (
                <>These terms shall be governed by and construed in accordance with the laws of <strong>England and Wales</strong>. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts of England and Wales.</>
              )}
            </Typography>

            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              <strong>Note:</strong> For customers with a registered entity in Pakistan, the laws of Pakistan shall apply.
            </Typography>
          </Paper>

          {/* 17. CONTACT INFORMATION - Updated */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h2" gutterBottom>17. Contact Information</Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={700}>📧 General Inquiries & Support</Typography>
                <Typography variant="body2">Email: support@fluxerp.co.uk</Typography>
                <Typography variant="body2">Phone: {contact.phone}</Typography>
                <Typography variant="body2">Address: {contact.addressLine1}, {contact.addressLine2}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight={700}>⚖️ Legal & Compliance</Typography>
                <Typography variant="body2">Legal Department: support@fluxerp.co.uk</Typography>
                <Typography variant="body2">Data Protection Officer: support@fluxerp.co.uk</Typography>
                <Typography variant="body2">Security Incidents: support@fluxerp.co.uk</Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 3, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Response Times:</strong><br />
                • Support Requests: Within 2 business hours (Priority), 24 hours (Normal)<br />
                • Legal Inquiries: Within 5 business days<br />
                • Security Incidents: Immediate response (24/7)<br />
                • Data Subject Requests: Within 30 days (GDPR), 45 days (CCPA)
              </Typography>
            </Box>
          </Paper>

          {/* Effective Date and Updates */}
          <Paper sx={{ p: 4, bgcolor: 'grey.50' }}>
            <Typography variant="body2" align="center" color="textSecondary">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}<br />
              <strong>Version:</strong> Version 1.0<br />
              <strong>Last Reviewed:</strong> Quarterly<br />
              We reserve the right to update these Terms. Material changes will be communicated via email and in-app notification.<br />
              <strong>Previous versions:</strong> Available upon request at support@fluxerp.co.uk
            </Typography>
          </Paper>
        </Box>
      </Container>
    </WebsiteLayout>
  );
};

export default TermsOfServicePage;